import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import Reveal from "../components/Reveal";
import Marquee from "../components/Marquee";

const CHAPTERS = [
  {
    n: "01",
    title: "Une association née d'une conviction",
    blocks: [
      {
        t: "p",
        text: "Joy Messenger est née d'un désir simple mais puissant : défendre, produire et promouvoir le jazz et les musiques parallèles — celles qui ne rentrent pas dans les cases, qui osent, qui expérimentent.",
      },
      {
        t: "p",
        text: "Plus qu'une simple organisation, c'est un lieu d'échange, une aventure humaine au service de la création musicale indépendante.",
      },
    ],
  },
  {
    n: "02",
    title: "Notre mission : faire vivre la musique libre",
    blocks: [
      { t: "p", text: "Notre mission, c'est de faire vivre la musique libre :" },
      {
        t: "ul",
        items: ["celle qui échappe au format,", "celle qui respire,", "celle qui relie les gens entre eux."],
      },
      {
        t: "p",
        text: "Nous croyons en une musique vivante, sincère, indépendante — une musique qui ne se contente pas d'être écoutée, mais qui se vit, se partage, et transforme ceux qui la rencontrent.",
      },
    ],
  },
  {
    n: "03",
    title: "Pédagogie, transmission et histoire de la musique",
    blocks: [
      { t: "p", text: "Au cœur de Joy Messenger, il y a aussi une volonté forte de transmission. Nous organisons :" },
      {
        t: "ul",
        items: [
          "des masterclass avec des musicien·ne·s confirmé·e·s,",
          "des workshops thématiques (improvisation, écriture, jeu en petite formation, big band…),",
          "des ateliers d'initiation et de perfectionnement ouverts à tous les niveaux.",
        ],
      },
      { t: "p", text: "Ces temps pédagogiques s'appuient sur l'histoire du jazz et des musiques apparentées :" },
      {
        t: "ul",
        items: [
          "compréhension des grands courants (swing, bebop, free, fusion, scènes contemporaines…),",
          "écoute et analyse d'œuvres de référence,",
          "mise en perspective des contextes sociaux et culturels qui ont fait naître ces musiques.",
        ],
      },
      {
        t: "p",
        text: "Pour nous, le jazz est un modèle d'apprentissage : il apprend à écouter, à prendre sa place dans un groupe, à accepter le risque, à construire ensemble.",
      },
    ],
  },
  {
    n: "04",
    title: "Jazz en milieu rural et défavorisé",
    blocks: [
      {
        t: "p",
        text: "Joy Messenger porte une attention particulière aux territoires ruraux et aux milieux défavorisés, où l'offre culturelle et pédagogique est souvent plus rare. Nous développons des actions :",
      },
      {
        t: "ul",
        items: [
          "en zones rurales, avec des stages, résidences et concerts qui transforment temporairement un village en lieu de création et de rencontre,",
          "dans des quartiers prioritaires et des contextes sociaux fragiles, en proposant des ateliers accessibles à des jeunes qui n'ont pas ou peu accès à l'enseignement musical traditionnel.",
        ],
      },
      {
        t: "p",
        text: "Musique de partage et d'échange, il est le terrain de jeu pour l'aide à la cohésion en terrain sensible et aide à établir une entente fraternelle.",
      },
    ],
  },
  {
    n: "05",
    title: "Des projets vivants, sincères et indépendants",
    blocks: [
      {
        t: "p",
        text: "Tous les projets portés par Joy Messenger ont un point commun : ils sont vivants, sincères et indépendants.",
      },
      {
        t: "p",
        text: "Qu'il s'agisse de concerts, de résidences, de créations originales, de masterclass ou d'ateliers en milieu rural, chaque initiative est pensée comme un espace de liberté — pour les musicien·ne·s comme pour les participant·e·s et le public.",
      },
    ],
  },
  {
    n: "06",
    title: "Un lieu d'échange et de rencontre",
    blocks: [
      { t: "p", text: "Joy Messenger, c'est aussi un lieu d'échange :" },
      {
        t: "ul",
        items: [
          "entre musicien·ne·s de différentes générations et horizons,",
          "entre artistes, élèves et publics,",
          "entre pratiques, esthétiques et sensibilités.",
        ],
      },
      {
        t: "p",
        text: "Nous organisons des temps de rencontre, de discussion, de transmission — parce que la musique ne se résume pas à la scène : elle se construit aussi en amont, dans la confiance, l'écoute et la curiosité mutuelle.",
      },
    ],
  },
  {
    n: "07",
    title: "Une aventure humaine au service de la création",
    blocks: [
      {
        t: "p",
        text: "Derrière chaque concert, chaque workshop, chaque note, il y a des femmes et des hommes qui y croient, qui s'investissent, qui prennent des risques.",
      },
      {
        t: "p",
        text: "Joy Messenger, c'est cette aventure humaine : une équipe, des bénévoles, des partenaires, des soutiens qui mettent leur énergie au service d'une idée — que la musique indépendante, libre et exigeante a toute sa place, y compris dans les territoires ruraux et les milieux défavorisés.",
      },
    ],
  },
];

const HELLOASSO_URL = "https://www.helloasso.com/associations/joy-messenger/collectes/soutien-a-la-creation-musicale-independante";

const JOIN_ACTIONS = [
  "en assistant à nos concerts, masterclass et ateliers,",
  "en relayant nos projets sur les réseaux,",
  <>
    en faisant un don (à partir de 1&nbsp;€) via notre{" "}
    <a
      href={HELLOASSO_URL}
      target="_blank"
      rel="noreferrer"
      data-testid="about-helloasso-link"
      className="text-jm-amber underline decoration-jm-brass/40 underline-offset-4 hover:text-jm-bone transition-colors"
    >
      page HelloAsso
    </a>
    ,
  </>,
  "ou simplement en venant échanger avec nous pour imaginer ensemble de nouvelles actions pédagogiques et territoriales.",
];

export default function About() {
  return (
    <main data-testid="about-page" className="pt-28 sm:pt-36">
      <div className="jm-container">
        <Reveal>
          <p className="jm-overline mb-5">À propos</p>
          <h1 className="font-serif font-light text-4xl sm:text-5xl lg:text-6xl text-jm-bone leading-[1.08] max-w-4xl">
            « Plusieurs projets, <em className="italic text-jm-brass">une même aventure artistique. »</em>
          </h1>
        </Reveal>
      </div>

      <Marquee className="mt-16" items={["Liberté", "Authenticité", "Improvisation", "Reliance", "Création"]} />

      <div className="jm-container py-20 sm:py-28">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <p className="jm-overline mb-5">Une conviction simple</p>
            <p className="text-jm-smoke leading-relaxed">
              Joy Messenger est née d'une conviction simple : une musique libre a besoin d'une structure libre.
              L'association accompagne cinq projets — SIRIN CODAL, NICODRI TRIO, WIB ORGANIC TRIO,
              BAM THE HOWLS et SMALLCO — de la composition à la diffusion.
            </p>
            <div className="mt-10 grid grid-cols-2 gap-6">
              {[
                ["5", "projets artistiques"],
                ["3", "esthétiques du jazz"],
                ["1", "maison indépendante"],
                ["100 %", "création maison"],
              ].map(([n, l]) => (
                <div key={l} className="border-l border-jm-brass/25 pl-4">
                  <p className="font-serif text-4xl text-jm-amber">{n}</p>
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-jm-mute mt-2">{l}</p>
                </div>
              ))}
            </div>
          </Reveal>
          <div className="lg:col-span-8 divide-y divide-jm-brass/10 border-y border-jm-brass/10">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={0.06 * i}>
                <div className="py-10 flex flex-col sm:flex-row gap-4 sm:gap-12" data-testid={`about-chapter-${c.n}`}>
                  <span className="font-mono text-xs text-jm-amber tracking-[0.2em] pt-2">{c.n}</span>
                  <div>
                    <h2 className="font-serif text-2xl sm:text-3xl text-jm-bone">{c.title}</h2>
                    <div className="mt-4 max-w-xl">
                      {c.blocks.map((b, j) =>
                        b.t === "p" ? (
                          <p key={j} className="text-jm-smoke leading-relaxed mt-3 first:mt-0">
                            {b.text}
                          </p>
                        ) : (
                          <ul key={j} className="mt-3 space-y-1.5">
                            {b.items.map((item, k) => (
                              <li key={k} className="flex items-start gap-3 text-jm-smoke leading-relaxed">
                                <span className="text-jm-amber text-[9px] mt-2 shrink-0">◆</span>
                                <span>{item}</span>
                              </li>
                            ))}
                          </ul>
                        )
                      )}
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>

        {/* Rejoindre l'aventure */}
        <Reveal>
          <div className="mt-20 border border-jm-brass/25 bg-jm-coal p-8 sm:p-12" data-testid="about-join">
            <p className="jm-overline mb-4">Rejoindre l'aventure</p>
            <h2 className="font-serif text-3xl sm:text-4xl text-jm-bone max-w-2xl">
              Si cette vision résonne en vous, vous pouvez soutenir Joy Messenger&nbsp;:
            </h2>
            <ul className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3 max-w-3xl">
              {JOIN_ACTIONS.map((a, i) => (
                <li key={i} className="flex items-start gap-3 text-jm-smoke leading-relaxed">
                  <span className="text-jm-amber text-[9px] mt-2 shrink-0">◆</span>
                  <span>{a}</span>
                </li>
              ))}
            </ul>
            <p className="mt-8 text-jm-smoke leading-relaxed max-w-2xl">
              Parce que chaque geste, même modeste, contribue à faire vivre cette musique qui nous tient à cœur —
              et à la rendre accessible à celles et ceux qui en ont le plus besoin.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/soutenir"
                data-testid="about-support-cta"
                className="inline-flex items-center gap-2 bg-jm-amber text-jm-ink px-7 py-3.5 text-sm font-medium tracking-wide hover:bg-jm-gold transition-colors duration-300"
              >
                Soutenir la maison <ArrowUpRight size={14} />
              </Link>
              <Link
                to="/artistes"
                data-testid="about-artists-cta"
                className="inline-flex items-center gap-2 border border-jm-brass/40 text-jm-bone px-7 py-3.5 text-sm tracking-wide hover:border-jm-amber hover:text-jm-amber transition-colors duration-300"
              >
                Découvrir les cinq projets
              </Link>
              <Link
                to="/contact"
                data-testid="about-contact-cta"
                className="inline-flex items-center gap-2 border border-jm-brass/40 text-jm-bone px-7 py-3.5 text-sm tracking-wide hover:border-jm-amber hover:text-jm-amber transition-colors duration-300"
              >
                Nous écrire
              </Link>
            </div>
          </div>
        </Reveal>
      </div>
    </main>
  );
}
